'use client';

import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, ChevronDown } from 'lucide-react';

import { PublicLink } from '@/components/PublicLink';
import { StickyHeaderShell } from '@/components/StickyHeaderShell';
import { useAnimation } from '@/contexts/AnimationContext';
import { useAuth } from '@/contexts/AuthContext';
import { CLASSIC_SITE_URL } from '@/lib/classicSite';
import { getVersionedPublicAssetUrl } from '@/lib/staticAssetUrls';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  adminNavLinks,
  authenticatedNavLinks,
  marketingNavLinks,
  type NavLink,
} from '@/components/site/navigation';

type NavbarProps = {
  variant?: 'marketing' | 'app' | 'utility';
};

const BRAND_MARK_URL = getVersionedPublicAssetUrl('s-shot-mark.svg');

function getUserInitials(name?: string | null) {
  if (!name) {
    return 'SS';
  }

  const segments = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (segments.length === 0) {
    return 'SS';
  }

  return segments
    .slice(0, 2)
    .map((segment) => segment.charAt(0).toUpperCase())
    .join('');
}

function isActivePath(pathname: string, href: string) {
  if (href === '/settings') {
    return (
      pathname === '/settings' ||
      pathname.startsWith('/settings/') ||
      pathname === '/profile' ||
      pathname.startsWith('/profile/') ||
      pathname === '/api-keys' ||
      pathname.startsWith('/api-keys/')
    );
  }

  if (href === '/help') {
    return pathname === '/help' || pathname.startsWith('/help/') || pathname === '/contact';
  }

  if (href === '/datasets' || href === '/datasets/my') {
    return (
      pathname === '/datasets' ||
      pathname === '/datasets/my' ||
      pathname.startsWith('/datasets/') ||
      pathname === '/marketplace-intelligence' ||
      pathname.startsWith('/marketplace-intelligence/')
    );
  }

  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

function AccountInitials({ name }: { name?: string | null }) {
  const initials = useMemo(() => getUserInitials(name), [name]);

  return (
    <span className="flex h-[var(--site-account-avatar-size)] w-[var(--site-account-avatar-size)] items-center justify-center rounded-full border border-[color:var(--site-border-soft)] bg-white/80 text-[length:var(--site-account-avatar-font-size)] font-semibold text-[var(--site-ink-strong)]">
      {initials}
    </span>
  );
}

type OutsideClickRef = {
  current: HTMLElement | null;
};

function useClickOutside(refs: OutsideClickRef[], handler: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (refs.some((ref) => ref.current?.contains(target))) {
        return;
      }

      handler();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handler();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, handler, refs]);
}

function MobileNav({
  links,
  isAuthenticated,
  isAdmin,
  onLogout,
  onClose,
}: {
  links: NavLink[];
  isAuthenticated: boolean;
  isAdmin: boolean;
  onLogout: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-5 pt-4">
      <div className="space-y-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--site-ink-subtle)]">
          Навигация
        </div>
        <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">
          Переключайтесь между студией, шаблонами, датасетами и настройками аккаунта.
        </p>
      </div>
      <div className="flex flex-col divide-y divide-[color:var(--site-border-soft)]">
        {links.map((link) => (
          <PublicLink
            key={link.href}
            href={link.href}
            onClick={onClose}
            className="flex items-center justify-between py-3 text-base font-medium text-[var(--site-ink-strong)] transition-colors hover:text-[var(--site-ink)]"
          >
            <span>{link.label}</span>
            <span className="text-sm text-[var(--site-ink-subtle)]">→</span>
          </PublicLink>
        ))}
        <a
          href={CLASSIC_SITE_URL}
          onClick={onClose}
          className="flex items-center justify-between py-3 text-base font-medium text-[var(--site-ink-strong)] transition-colors hover:text-[var(--site-ink)]"
        >
          <span>Классическая версия</span>
          <span className="text-sm text-[var(--site-ink-subtle)]">→</span>
        </a>
      </div>
      {isAuthenticated ? (
        <div className="space-y-3 border-t border-[color:var(--site-border-soft)] pt-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--site-ink-subtle)]">
            Аккаунт
          </div>
          <div className="flex flex-col divide-y divide-[color:var(--site-border-soft)]">
              <PublicLink
                href="/profile"
                onClick={onClose}
                className="py-3 text-base font-medium text-[var(--site-ink-strong)] transition-colors hover:text-[var(--site-ink)]"
              >
                Профиль
            </PublicLink>
              <PublicLink
                href="/api-keys"
                onClick={onClose}
                className="py-3 text-base font-medium text-[var(--site-ink-strong)] transition-colors hover:text-[var(--site-ink)]"
              >
                API-ключи
            </PublicLink>
              <PublicLink
                href="/settings"
                onClick={onClose}
                className="py-3 text-base font-medium text-[var(--site-ink-strong)] transition-colors hover:text-[var(--site-ink)]"
              >
                Настройки
              </PublicLink>
          {isAdmin ? (
            <details className="py-3">
              <summary className="cursor-pointer list-none text-base font-medium text-[var(--site-ink-strong)]">
                Администрирование
              </summary>
              <div className="mt-3 flex flex-col gap-2">
                {adminNavLinks.map((link) => (
                  <PublicLink
                    key={link.href}
                    href={link.href}
                    onClick={onClose}
                    className="rounded-xl px-2 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                  >
                    {link.label}
                  </PublicLink>
                ))}
              </div>
            </details>
          ) : null}
          <button
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="py-3 text-left text-base font-medium text-red-600"
          >
            Выйти
          </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2 pt-1">
          <PublicLink href="/login">
            <Button variant="outline" className="w-full">
              Войти
            </Button>
          </PublicLink>
          <PublicLink href="/signup">
            <Button className="w-full">Создать аккаунт</Button>
          </PublicLink>
        </div>
      )}
    </div>
  );
}

export function Navbar({ variant: _variant = 'marketing' }: NavbarProps) {
  const pathname = usePathname();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { enableAnimations, toggleAnimations } = useAnimation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);

  const navLinks = useMemo(() => {
    if (!isAuthenticated) {
      return marketingNavLinks;
    }

    return authenticatedNavLinks;
  }, [isAuthenticated]);

  const maxWidthClass = 'site-shell-wide';
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);
  const closeAccountMenu = useCallback(() => {
    setAccountMenuOpen(false);
  }, []);
  const toggleAccountMenu = useCallback(() => {
    setAccountMenuOpen((prev) => !prev);
  }, []);
  const navShellStyle = {
    background: 'linear-gradient(180deg, rgba(251, 248, 242, 0.62), rgba(251, 248, 242, 0.5))',
    backdropFilter: 'blur(18px)',
  };
  const scrolledNavShellStyle = {
    background: 'linear-gradient(180deg, rgba(251, 248, 242, 0.84), rgba(251, 248, 242, 0.74))',
    backdropFilter: 'blur(22px)',
  };

  useClickOutside([accountMenuRef, accountButtonRef], closeAccountMenu, accountMenuOpen);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    // Keep the global header revealed while the mobile menu is open. The menu
    // overlay is a `position: fixed` descendant of `.site-sticky-header`; if the
    // header's directional-reveal transform engages (on scroll-down), the header
    // becomes the overlay's containing block and pulls the open menu off-screen.
    // This flag lets globals.css suppress the hide transform while the menu is
    // open (and the header also holds the menu's close button, so it should stay
    // visible regardless).
    const root = document.documentElement;
    root.setAttribute('data-nav-menu-open', '');

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      root.removeAttribute('data-nav-menu-open');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setAccountMenuOpen(false);
  }, [pathname]);

  return (
    <header className="site-sticky-header">
      <StickyHeaderShell
        className={cn(
          'mx-auto rounded-[var(--site-header-radius)] border px-[var(--site-header-shell-px)] py-[var(--site-header-shell-py)] data-[scrolled=false]:border-[color:rgba(255,255,255,0.22)] data-[scrolled=true]:border-[color:rgba(23,38,59,0.06)]',
          maxWidthClass,
        )}
        scrolledStyle={scrolledNavShellStyle}
        style={navShellStyle}
      >
        <div className="flex items-center justify-between gap-[var(--site-header-gap)]">
          <div className="flex min-w-0 items-center gap-[var(--site-header-brand-gap)]">
            <PublicLink href="/" className="flex min-h-11 min-w-0 items-center gap-[var(--site-header-brand-gap)]">
              <div className="flex h-[var(--site-brand-mark-size)] w-[var(--site-brand-mark-size)] items-center justify-center rounded-[var(--site-brand-mark-radius)] border border-[color:rgba(23,38,59,0.08)] bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(243,248,250,0.9))] shadow-[0_12px_28px_rgba(23,38,59,0.08)]">
                <Image
                  src={BRAND_MARK_URL}
                  alt="S-SHOT logo"
                  width={38}
                  height={38}
                  className="h-[var(--site-brand-mark-icon-size)] w-[var(--site-brand-mark-icon-size)] object-contain"
                  priority
                />
              </div>
              <div className="min-w-0">
                <div className="text-[length:var(--site-brand-kicker-font-size)] font-semibold uppercase tracking-[0.2em] text-[var(--site-ink-subtle)]">
                  S-SHOT
                </div>
                <div className="hidden truncate text-[length:var(--site-brand-title-font-size)] text-[var(--site-ink-muted)] sm:block">
                  Студия структурированных веб-данных
                </div>
              </div>
            </PublicLink>

            <nav
              className={cn(
                'ml-[var(--site-header-nav-offset)] hidden items-center gap-2 rounded-full border p-[var(--site-header-nav-pad)] xl:flex',
                'border-[color:rgba(255,255,255,0.24)] bg-white/38',
              )}
            >
              {navLinks.map((link) => {
                const active = isActivePath(pathname, link.href);
                return (
                  <PublicLink
                    key={link.href}
                    href={link.href}
                    className={cn(
                      'inline-flex h-11 items-center whitespace-nowrap rounded-full px-[var(--site-header-nav-pill-px)] text-[length:var(--site-header-nav-pill-font-size)] font-medium leading-none transition-colors',
                      active
                        ? 'bg-[var(--site-primary-fill)] text-[var(--site-primary-text)] shadow-[var(--site-shadow-button)]'
                        : 'text-[var(--site-ink)] hover:bg-white/80 hover:text-[var(--site-ink-strong)]',
                    )}
                  >
                    {link.label}
                  </PublicLink>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-[var(--site-header-actions-gap)]">
            <Button asChild variant="outline" size="sm" className="hidden min-h-11 whitespace-nowrap lg:inline-flex">
              <a href={CLASSIC_SITE_URL}>Классическая версия</a>
            </Button>
            {isLoading ? (
              <div
                aria-hidden="true"
                className="hidden lg:block"
                style={{ minWidth: 'var(--site-account-button-height)', height: 'var(--site-account-button-height)' }}
              />
            ) : isAuthenticated ? (
              <div ref={accountMenuRef} className="relative hidden lg:block">
                <button
                  ref={accountButtonRef}
                  type="button"
                  onClick={toggleAccountMenu}
                  aria-expanded={accountMenuOpen}
                  aria-controls={accountMenuOpen ? 'account-menu-panel' : undefined}
                  aria-haspopup="menu"
                  className="inline-flex h-[var(--site-account-button-height)] items-center gap-[var(--site-header-brand-gap)] rounded-full border border-[color:var(--site-border-soft)] bg-white/75 px-[calc(var(--site-button-px-sm)-0.1rem)] pr-[var(--site-button-px)] text-[length:var(--site-auth-link-font-size)] font-medium text-[var(--site-ink-strong)] shadow-[var(--site-shadow-soft)] transition-colors hover:bg-white/90"
                >
                  <AccountInitials name={user?.name} />
                  <span className="hidden sm:inline">{user?.name ?? 'Аккаунт'}</span>
                  <ChevronDown className="size-4 text-[var(--site-ink-subtle)]" />
                </button>

                {accountMenuOpen ? (
                  <div
                    id="account-menu-panel"
                    data-testid="account-menu-panel"
                    className="absolute right-0 top-[calc(100%+0.75rem)] z-50 flex max-h-[calc(100vh-6.5rem)] w-72 flex-col overflow-hidden rounded-[1.25rem] border border-[color:var(--site-border-soft)] bg-[var(--site-surface-hover)] p-2 shadow-[var(--site-shadow-card)]"
                  >
                    <div className="shrink-0 px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--site-ink-subtle)]">
                        Мой аккаунт
                      </div>
                      <div className="mt-1 text-sm text-[var(--site-ink-muted)]">
                        Быстрый доступ к аккаунту, биллингу и настройкам.
                      </div>
                    </div>
                    <div
                      data-testid="account-menu-scroll-area"
                      className="min-h-0 overflow-y-auto overscroll-contain pr-1"
                    >
                      <div className="grid gap-1">
                        <PublicLink
                          href="/dashboard"
                          onClick={closeAccountMenu}
                          className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                        >
                          Панель
                        </PublicLink>
                        <PublicLink
                          href="/profile"
                          onClick={closeAccountMenu}
                          className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                        >
                          Профиль
                        </PublicLink>
                        <PublicLink
                          href="/api-keys"
                          onClick={closeAccountMenu}
                          className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                        >
                          API-ключи
                        </PublicLink>
                        <PublicLink
                          href="/billing"
                          onClick={closeAccountMenu}
                          className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                        >
                          Биллинг и лимиты
                        </PublicLink>
                        <PublicLink
                          href="/settings"
                          onClick={closeAccountMenu}
                          className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                        >
                          Настройки
                        </PublicLink>
                        {user?.isAdmin ? (
                          <div className="px-3 pt-2">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--site-ink-subtle)]">
                              Администрирование
                            </div>
                            <div className="mt-2 grid gap-1">
                              {adminNavLinks.map((link) => (
                                <PublicLink
                                  key={link.href}
                                  href={link.href}
                                  onClick={closeAccountMenu}
                                  className="rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] hover:bg-[var(--site-surface-muted)]"
                                >
                                  {link.label}
                                </PublicLink>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div
                      data-testid="account-menu-footer"
                      className="mt-2 shrink-0 border-t border-[color:var(--site-border-soft)] px-3 pt-2 pb-1"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          toggleAnimations();
                        }}
                        className="flex w-full items-center justify-between rounded-xl px-0 py-2 text-sm text-[var(--site-ink)] hover:text-[var(--site-ink-strong)]"
                      >
                        <span>Анимации</span>
                        <span className={enableAnimations ? 'text-emerald-600' : 'text-[var(--site-ink-subtle)]'}>
                          {enableAnimations ? 'ВКЛ' : 'ВЫКЛ'}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          closeAccountMenu();
                          logout();
                        }}
                        className="mt-1 flex w-full items-center justify-start rounded-xl px-0 py-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Выйти
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="hidden items-center gap-[var(--site-header-actions-gap)] lg:flex">
                <PublicLink
                  href="/login"
                  className="inline-flex min-h-11 items-center text-[length:var(--site-auth-link-font-size)] font-medium text-[var(--site-ink)] hover:text-[var(--site-ink-strong)]"
                >
                  Войти
                </PublicLink>
                <PublicLink href="/signup">
                  <Button className="min-h-11">Создать аккаунт</Button>
                </PublicLink>
              </div>
            )}

            <Button
              variant="outline"
              size="icon"
              className="xl:hidden"
              aria-label="Открыть навигацию"
              aria-expanded={mobileMenuOpen}
              onClick={toggleMobileMenu}
            >
              <Menu className="size-5" />
              <span className="sr-only">Открыть навигацию</span>
            </Button>
          </div>
        </div>
      </StickyHeaderShell>
      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            aria-label="Закрыть навигацию"
            className="absolute inset-0 cursor-default bg-[color:rgba(17,24,39,0.45)]"
            onClick={closeMobileMenu}
          />
          <div className="absolute right-0 top-0 h-full w-[min(100vw,22rem)] overflow-y-auto border-l border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-4 py-4 shadow-[var(--site-shadow-card)] sm:px-5">
            <div className="flex items-center justify-between gap-3 border-b border-[color:var(--site-border-soft)] pb-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--site-ink-subtle)]">
                  Навигация
                </div>
                <div className="text-sm text-[var(--site-ink-muted)]">
                  S-SHOT и аккаунт
                </div>
              </div>
              <Button variant="outline" size="icon" aria-label="Закрыть навигацию" onClick={closeMobileMenu}>
                <span className="text-xl leading-none">×</span>
              </Button>
            </div>
            <MobileNav
              links={navLinks}
              isAuthenticated={isAuthenticated}
              isAdmin={Boolean(user?.isAdmin)}
              onLogout={logout}
              onClose={closeMobileMenu}
            />
          </div>
        </div>
      ) : null}
    </header>
  );
}
